<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("variant_list") }}</h4>
        </div>
      </div>
    </div>

    <ThemeDataTable
      table-id="VariantListTable"
      :fetch-data="fetchVariantsPage"
      :filters="tableFilters"
      :empty-title="$t('no_variants_found')"
      :empty-hint="$t('no_variants_found_list_hint')"
    >
      <template #default="{ rows }">
        <thead class="thead-light">
          <tr>
            <th>{{ $t("item_code") }}</th>
            <th>{{ $t("item_name") }}</th>
            <th>{{ $t("category") }}</th>
            <th>{{ $t("sub_category") }}</th>
            <th>{{ $t("variant") }}</th>
            <th>{{ $t("barcode_qr_code") }}</th>
            <th>{{ $t("sku") }}</th>
            <th>{{ $t("selling_price") }}</th>
            <th>{{ $t("discount") }}</th>
            <th>{{ $t("vat_group") }}</th>
            <th>{{ $t("stock_quantity") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="variant in rows" :key="variant.variant_id">
            <td>{{ variant.item_code }}</td>
            <td>
              <div class="d-flex align-items-center">
                <span class="avatar avatar-md me-2">
                  <img
                    v-if="variant.small_thumb || variant.image"
                    :src="$getImageUrl(variant.small_thumb || variant.image)"
                    alt="Variant Image"
                  />
                  <span
                    v-else
                    class="avatar-title rounded bg-light text-secondary d-flex align-items-center justify-content-center w-100 h-100"
                  >
                    <i class="ti ti-photo"></i>
                  </span>
                </span>
                <span>{{ variant.item_name }}</span>
              </div>
            </td>
            <td>{{ variant.category_name }}</td>
            <td>{{ variant.sub_category_name }}</td>
            <td>{{ formatAttributes(variant.variant_attributes) }}</td>
            <td>{{ variant.barcode_qr_code }}</td>
            <td>{{ variant.sku }}</td>
            <td>{{ formatMoney(variant.selling_price) }}</td>
            <td>{{ variant.discount }}</td>
            <td>{{ variant.vat_group_name }}</td>
            <td>{{ variant.current_stock ?? variant.stock_quantity ?? 0 }}</td>
          </tr>
        </tbody>
      </template>
    </ThemeDataTable>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import ThemeDataTable from '@/components/common/ThemeDataTable.vue';
import { truncateMoney2 } from '@/utils';

export default {
  components: {
    ThemeDataTable,
  },
  data() {
    return {
      filterOptions: {
        item_code: [],
        item_name: [],
        category: [],
        sub_category: [],
        vat_group: [],
      },
    };
  },
  computed: {
    tableFilters() {
      return [
        {
          key: 'item_code',
          label: this.$t('item_code'),
          type: 'select2',
          options: this.filterOptions.item_code,
        },
        {
          key: 'item_name',
          label: this.$t('item_name'),
          type: 'select2',
          options: this.filterOptions.item_name,
        },
        {
          key: 'category',
          label: this.$t('category'),
          options: this.filterOptions.category,
        },
        {
          key: 'sub_category',
          label: this.$t('sub_category'),
          options: this.filterOptions.sub_category,
        },
        {
          key: 'vat_group',
          label: this.$t('vat_group'),
          options: this.filterOptions.vat_group,
        },
      ];
    },
  },
  methods: {
    formatMoney(amount) {
      return truncateMoney2(amount);
    },

    formatAttributes(attributes) {
      if (!attributes || !attributes.length) return '—';
      return attributes
        .map((attr) => `${attr.attribute}: ${attr.value}`)
        .join(' | ');
    },

    async fetchFilterOptions() {
      try {
        const response = await this.$axios.get(apiUrls.item.all_variants, {
          params: { filter_options: 1 },
        });
        const data = response.data || {};
        this.filterOptions.item_code = data.item_code || [];
        this.filterOptions.item_name = data.item_name || [];
        this.filterOptions.category = data.category || [];
        this.filterOptions.sub_category = data.sub_category || [];
        this.filterOptions.vat_group = data.vat_group || [];
      } catch (error) {
        console.error('Error fetching variant filter options:', error);
      }
    },

    async fetchVariantsPage({ draw, start, length, search, orderColumn, orderDir, filters }) {
      const params = {
        draw,
        start,
        length,
        'search[value]': search || '',
        'order[0][column]': orderColumn,
        'order[0][dir]': orderDir,
        ...filters,
      };

      const response = await this.$axios.get(apiUrls.item.all_variants, { params });
      const payload = response.data || {};

      return {
        data: payload.data || [],
        recordsTotal: payload.recordsTotal || 0,
        recordsFiltered: payload.recordsFiltered || 0,
      };
    },
  },
  created() {
    this.fetchFilterOptions();
  },
};
</script>
